import { FC, useEffect, useState } from 'react'

import qs from 'qs'
import { useMount } from 'react-use'

import { AnimatedInput } from 'core/components'
import { assert } from 'core/helpers'
import { useWrite } from 'core/hooks'
import { useBootstrapStore } from 'core/stores'
import { ExpectedError } from 'core/system/ErrorLogging'

import { requestOneTimeCode } from './loginApi'
import LoginFormCard from './LoginFormCard'
import { minDelay } from './loginHelpers'
import LoginSubmitButton from './LoginSubmitButton'
import OneTimeCodeModal from './OneTimeCode/OneTimeCodeModal'
import { formatOtcError } from './OneTimeCode/otcHelpers'

type CodeLoginProps = {
  onLogin: ({
    username,
    password,
    selectedAuthType,
  }: {
    username: string
    password: string
    selectedAuthType: 'oneTimeCodeEmail' | 'oneTimeCodeText'
  }) => void
}

const CodeLogin: FC<CodeLoginProps> = ({ onLogin }) => {
  const { company, userType, authType, forceAuthType } = useBootstrapStore() ?? {}
  const companyId = company?.id

  assert(userType && companyId, 'Bootstrap values are unset')

  const [username, setUsername] = useState(
    () => (qs.parse(window.location.search, { ignoreQueryPrefix: true })?.value as string) ?? '',
  )

  /*
  oneTimeCodeEmail => 'email' is the default OTC channel
  oneTimeCodeText => 'text' is the default channel
  */
  const initialChannel = authType === 'oneTimeCodeEmail' ? 'email' : 'text'
  const authValueLabel = authType === 'oneTimeCodeEmail' ? 'Email' : 'Phone number'

  const onRequestCodeToChannel = async (channel: 'email' | 'text') =>
    requestOneTimeCode({ username, companyId, userType, channel, force: forceAuthType })

  const [requestCode, requesting, resp, error, clear] = useWrite(() => {
    if (!username) throw new ExpectedError(`${authValueLabel} is required`)

    return minDelay(onRequestCodeToChannel(initialChannel), 750)
  })

  useEffect(() => {
    clear()
  }, [username, clear])

  // Remove the `value` query param from URL if it exists and update the browser history
  useMount(() => {
    const url = new URL(window.location.href)
    if (url.searchParams.has('value')) {
      url.searchParams.delete('value')
      window.history.pushState({}, '', url)
    }
  })

  return (
    <LoginFormCard>
      <AnimatedInput
        disabled={requesting}
        label={authValueLabel}
        maskedType={authType === 'oneTimeCodeEmail' ? undefined : 'tel'}
        name={authType}
        onChange={(event) => {
          // BE should be case insensitive,
          // but also setting to lowercase for aesthetic reasons for the user
          setUsername(event.target.value.toLowerCase())
        }}
        onEnter={() => username && !requesting && requestCode()}
        placeholder=' '
        showError={error && !username}
        type='text'
        value={username}
      />

      <LoginSubmitButton
        disabled={!!resp}
        errorMessage={formatOtcError(error)}
        onSubmit={requestCode}
        sending={requesting}
        subtitle="We'll send you a verification code"
      />

      {resp && (
        <OneTimeCodeModal
          initialChannel={initialChannel}
          onClose={clear}
          onOneTimeCode={(code, authType) => onLogin({ username, password: code, selectedAuthType: authType })}
          onRequestCodeToChannel={onRequestCodeToChannel}
          userType={userType}
        />
      )}
    </LoginFormCard>
  )
}

export default CodeLogin
